import { useDispatch, useSelector } from "react-redux";
import "../../../../styles/screens/client-visit-details/manage-module/SpeakersList.css";
import SpeakerCard from "../SpeakerCard";
import noTitanOfIndustry from "../../../../assets/no-titan-of-industry.svg";
import Loader from "../../../common-components/Loader";
import { useEffect, useState, useRef } from "react";
import {
  getAllAttendes,
  getAllSpeakers,
  resetErrorAlert,
} from "../../../../slices/SpeakerSlice";
import {
  ManageModulesStringLabels,
  TitansOfIndustryStringLabels,
} from "../../../../util/Strings";
import ErrorAlert from "../../../common-components/ErrorAlert";
import ErrorMessages from "../../../../util/ErrorMessages";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";

import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";

function SpeakersList({ isSpeaker = true }) {
  const dispatch = useDispatch();

  // Selectors for data and loading state
  const selectedOfficeId = useSelector((state) => state.officelist.selectedOfficeId);
  const speakerData = useSelector((state) => state.speakers.speakerData);
  const attendeeData = useSelector((state) => state.speakers.attendeeData);
  const totalSpeakers = useSelector((state) => state.speakers.totalSpeakers);
  const totalAttendees = useSelector((state) => state.speakers.totalAttendees);
  const speakersLoading = useSelector((state) => state.speakers.speakersLoading);
  const attendeesLoading = useSelector((state) => state.speakers.attendeesLoading);
  const errorMessageCode = useSelector((state) => state.speakers.errorCode);

  // Pagination & Search state
  const [input, setInput] = useState("");
  const [debouncedInput, setDebouncedInput] = useState("");
  const [page, setPage] = useState(1);
  const limit = 50;

  // State to track whether any dialog (Edit/Delete) is open in any card
  const [dialogOpen, setDialogOpen] = useState(false);

  // Ref for search input to keep focus and cursor
  const inputRef = useRef(null);

  // Track previous list length to detect deletion
  const prevListLengthRef = useRef(null);

  // Debounce the search input and reset page on typing
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInput(input.trim());
      setPage(1);
    }, 500);

    return () => clearTimeout(handler);
  }, [input]);

  // Reset page when selected office changes
  useEffect(() => {
    setPage(1);
  }, [selectedOfficeId]);

  // Detect deletion: when list becomes empty on current page, go back a page or re-fetch
  const listData = isSpeaker ? speakerData : attendeeData;
  const totalItems = isSpeaker ? totalSpeakers : totalAttendees;

  useEffect(() => {
    // If the list was previously non-empty and is now empty after a deletion
    if (
      prevListLengthRef.current !== null &&
      prevListLengthRef.current > 0 &&
      listData.length === 0 &&
      page > 1
    ) {
      // Go back one page
      setPage((prev) => prev - 1);
    } else if (
      prevListLengthRef.current !== null &&
      prevListLengthRef.current > 0 &&
      listData.length < prevListLengthRef.current &&
      selectedOfficeId
    ) {
      // Item was deleted but page still has items, re-fetch to fill the gap
      if (isSpeaker) {
        dispatch(
          getAllSpeakers({
            officeId: selectedOfficeId,
            page,
            limit,
            search: debouncedInput,
          })
        );
      } else {
        dispatch(
          getAllAttendes({
            officeId: selectedOfficeId,
            page,
            limit,
            search: debouncedInput,
          })
        );
      }
    }

    // Update the ref with current length
    prevListLengthRef.current = listData.length;
  }, [listData.length]);

  // Fetch speakers or attendees whenever dependencies (filters) change
  useEffect(() => {
    if (!selectedOfficeId) return;

    if (isSpeaker) {
      dispatch(
        getAllSpeakers({
          officeId: selectedOfficeId,
          page,
          limit,
          search: debouncedInput,
        })
      );
    } else {
      dispatch(
        getAllAttendes({
          officeId: selectedOfficeId,
          page,
          limit,
          search: debouncedInput,
        })
      );
    }
  }, [dispatch, isSpeaker, selectedOfficeId, page, limit, debouncedInput]);

  // Close error alert on user action
  const handleCloseErrorAlert = () => {
    dispatch(resetErrorAlert());
  };

  const totalPages = Math.ceil(totalItems / limit);
  const isLoading = isSpeaker ? speakersLoading : attendeesLoading;

  // ✅ Compute the "start - end of total" range
  const startItem = totalItems === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, totalItems);
  const rangeText =
    totalItems === 0 ? "0" : `${startItem}-${endItem} of ${totalItems}`;

  // ✅ Show error alert WITHOUT unmounting the whole component
  if (errorMessageCode) {
    return (
      <ErrorAlert
        show={!!errorMessageCode}
        onClickHandle={handleCloseErrorAlert}
        title="Error"
        subTitle={ErrorMessages(errorMessageCode)}
      />
    );
  }

  return (
    <div className="speakers-list-container">
      <div className="speakers-list-heading">
        {isSpeaker
          ? `${TitansOfIndustryStringLabels.titansOfIndustry} (${rangeText})`
          : `${ManageModulesStringLabels.attendes} (${rangeText})`}
        <div className="speaker-searchContainer">
          <div className="speaker-searchIconText">
            <FontAwesomeIcon size="2xs" icon={faMagnifyingGlass} color="#D1D1D1" />
            <input
              ref={inputRef}
              className="search"
              placeholder="Search"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              aria-label="Search speakers or attendees"
              autoComplete="off"
            />
          </div>
        </div>
      </div>

      {/* ✅ Loader is now shown INSIDE the body, so the input stays mounted */}
      <div className="speakers-list-body">
        {isLoading ? (
          <Loader />
        ) : listData && listData.length > 0 ? (
          listData.map((item) => (
            <SpeakerCard
              isSpeaker={isSpeaker}
              data={item}
              key={item._id}
              onDialogOpen={() => setDialogOpen(true)}
              onDialogClose={() => setDialogOpen(false)}
            />
          ))
        ) : (
          <>
            <p style={{ textAlign: "center", color: "#777" }}>
              {isSpeaker ? "No speakers found." : "No attendees found."}
            </p>
            <img
              src={noTitanOfIndustry}
              alt="no titan of industry"
              className="no-img"
            />
          </>
        )}
      </div>

      {/* Pagination bar — only show if no dialog open and not loading */}
      {totalPages > 1 && !dialogOpen && !isLoading && (
        <div className="floating-pagination">
          <Stack spacing={2} sx={{ display: "flex", alignItems: "center" }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              showFirstButton
              showLastButton
              color="primary"
              disabled={isLoading}
              aria-label="pagination"
            />
          </Stack>
        </div>
      )}
    </div>
  );
}

export default SpeakersList;